import { useState, useEffect } from 'react'
import {
  newBaseService,
  requestByJsBridge,
  requestByFecth,
  getNetworkTimeoutPromise,
  NETWORK_SUCCESS_CODE
} from '../network/request'
import { isMock, isXpAPP } from '../utils/common'
import { GRAY_ENABLED, GRAY_FLAG, GRAY_TIMEOUT_SEC, GRAY_SESSION_KEY, GRAY_CHECK_API, GRAY_HIT_TTL_MS } from '../config'

function setGraySession(code: string, value: boolean) {
  return window.sessionStorage.setItem(
    GRAY_SESSION_KEY + code,
    value ? '1' : '0'
  )
}

function getGraySession(code: string) {
  const value = window.sessionStorage.getItem(GRAY_SESSION_KEY + code)
  if (value === '1') return true
  if (value === '0') return false
  return null
}

// 命中灰度的持久化标记（localStorage，带 TTL）：仅在「请求超时/失败」时兜底读取，
// 让已命中过的用户不因一次网络抖动/超时掉回旧版；正常有网时仍以接口/ session 结果为准。
const GRAY_HIT_PREFIX = '__GRAY_HIT__'

function setGrayHit(code: string) {
  try {
    window.localStorage.setItem(
      GRAY_HIT_PREFIX + code,
      String(Date.now() + GRAY_HIT_TTL_MS)
    )
  } catch (e) {
    // localStorage 不可用（隐私模式/写满）时静默忽略，不影响主流程
  }
}

function getGrayHit(code: string): boolean {
  try {
    const raw = window.localStorage.getItem(GRAY_HIT_PREFIX + code)
    if (!raw) return false
    const expire = Number(raw)
    if (!expire || Date.now() > expire) {
      window.localStorage.removeItem(GRAY_HIT_PREFIX + code)
      return false
    }
    return true
  } catch (e) {
    return false
  }
}

// 清除命中标记：未命中（含灰度回退返回 false）时调用，
// 确保回退后即便遇到超时兜底，也不会因旧标记误判为命中而回到新版
function removeGrayHit(code: string) {
  try {
    window.localStorage.removeItem(GRAY_HIT_PREFIX + code)
  } catch (e) {
    // 忽略
  }
}

const checkIsGrayPromiseCache: Record<string, Promise<boolean>> = {}

export function checkIsGray(code: string = GRAY_FLAG) {
  if (!GRAY_ENABLED) {
    return Promise.resolve(false);
  }

  if (!checkIsGrayPromiseCache[code]) {
    const isGrayFromSession = getGraySession(code)
    if (isGrayFromSession !== null) {
      console.log('[Gray]: Check is gray >>>> from session', isGrayFromSession)
      checkIsGrayPromiseCache[code] = Promise.resolve(isGrayFromSession)
      return checkIsGrayPromiseCache[code]
    }

    console.log('[Gray]: Check is gray >>>> from network start')

    const requestFunc = isMock() || !isXpAPP() ? requestByFecth : requestByJsBridge

    checkIsGrayPromiseCache[code] = Promise.race([
      requestFunc(`${newBaseService}/${GRAY_CHECK_API}`, {
        bizKey: code,
      }),
      getNetworkTimeoutPromise(GRAY_TIMEOUT_SEC),
    ])
      .then((res: any) => {
        if (res.code === NETWORK_SUCCESS_CODE) {
          const gray = res?.data?.gray
          // 显式校验 gray 为布尔：避免 data 为空/字段缺失时静默走旧版且被缓存，
          // 与网络错误一样统一交由「错误态」处理
          if (typeof gray !== 'boolean') {
            throw new Error(
              '[Gray]: invalid gray in response: ' + JSON.stringify(res?.data)
            )
          }
          console.log('[Gray]: Check is gray >>>> from network done', gray)
          setGraySession(code, gray)
          // 命中：持久化标记供后续超时/失败兜底；
          // 未命中（含灰度回退）：清除历史标记，确保回退后超时也不再误兜底为新版
          if (gray) setGrayHit(code)
          else removeGrayHit(code)
          return gray
        } else {
          throw new Error(`[Gray]: check failed, code=${res?.code}`);
        }
      })
      // 发生错误/超时：清除本次 promise 缓存(下次可重试)、不写 session，
      // 回落为非灰度 → 展示旧版页面
      .catch((error) => {
        delete checkIsGrayPromiseCache[code]
        // 超时/失败兜底：命中过的用户（localStorage 未过期）仍保持灰度，避免弱网/唤醒抖动掉回旧版；
        // 未命中过的用户回落旧版（合理）。正常有网时仍以接口结果为准，故灰度回退也能生效。
        const hit = getGrayHit(code)
        console.log(
          '[Gray]: Check is gray >>>> error, fallback to',
          hit ? 'cached-hit (new page)' : 'non-gray (old page)',
          error
        )
        return hit
      })
  }
  return checkIsGrayPromiseCache[code]
}

// 请求灰度策略钩子
export default function useGrayStrategy(code?: string) {
  const [isGray, setIsGray] = useState(false)
  // 灰度关闭时初始即就绪（isLoaded=true），避免走 loading 分支闪一帧
  const [isLoaded, setIsLoaded] = useState(!GRAY_ENABLED)

  useEffect(() => {
    // 灰度关闭：保持初始就绪(isLoaded=true) + 非灰度，不请求、不重置 loading（避免闪帧）
    if (!GRAY_ENABLED) return
    let alive = true
    setIsLoaded(false)
    checkIsGray(code)
      .then((flag) => {
        if (alive) setIsGray(flag)
      })
      .catch(() => {
        // 双保险：任何异常都回落非灰度（展示旧版）
        if (alive) setIsGray(false)
      })
      .finally(() => {
        if (alive) setIsLoaded(true)
      })
    return () => {
      alive = false
    }
  }, [code])

  return {
    isLoaded,
    isGray,
  }
}
